import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Machine } from '../../types/map';

interface SKUSelectionStepProps {
  selectedMachines: Machine[];
  skuCount: number;
  pricePerSKU: number;
  onSKUCountChange: (count: number) => void;
  onPricePerSKUChange: (price: number) => void;
  isAlreadyListed?: boolean;
}

export function SKUSelectionStep({
  selectedMachines,
  skuCount,
  pricePerSKU,
  onSKUCountChange,
  onPricePerSKUChange,
  isAlreadyListed = false,
}: SKUSelectionStepProps) {
  const totalMachines = selectedMachines.length || (isAlreadyListed ? 1 : 0);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Fetch listing prices based on selected machines' venue types
  useEffect(() => {
    const fetchListingPrice = async () => {
      if (selectedMachines.length === 0 && !isAlreadyListed) {
        onPricePerSKUChange(0);
        return;
      }

      // Get unique venue types from selected machines
      const venueTypes = Array.from(new Set(selectedMachines.map(m => m.venueType).filter(Boolean)));
      
      if (venueTypes.length === 0) {
        onPricePerSKUChange(0);
        return;
      }

      try {
        setLoadingPrice(true);
        const res = await fetch('/api/store/venue-type-listing-prices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ venueTypes }),
        });

        if (res.ok) {
          const priceMap: Record<string, number> = await res.json();
          
          // If all machines have the same venue type, use that price
          if (venueTypes.length === 1 && priceMap[venueTypes[0]]) {
            const priceInPounds = priceMap[venueTypes[0]] / 100;
            onPricePerSKUChange(priceInPounds);
          } else if (venueTypes.length > 1) {
            // If machines have different venue types, use the first available price
            // or average if multiple prices exist
            const prices = venueTypes
              .map(vt => priceMap[vt])
              .filter((p): p is number => p !== undefined)
              .map(p => p / 100);
            
            if (prices.length > 0) {
              // Use average price if multiple venue types
              const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;
              onPricePerSKUChange(avgPrice);
            } else {
              onPricePerSKUChange(0);
            }
          } else {
            onPricePerSKUChange(0);
          }
        } else {
          console.error('Failed to fetch listing prices');
          onPricePerSKUChange(0);
        }
      } catch (error) {
        console.error('Error fetching listing prices:', error);
        onPricePerSKUChange(0);
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchListingPrice();
  }, [selectedMachines, isAlreadyListed, onPricePerSKUChange]);

  const handleSKUCountChange = (value: number) => {
    if (value >= 0 && value <= 2) {
      onSKUCountChange(value);
    }
  };

  const listingFee = skuCount * pricePerSKU * totalMachines;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Select SKUs</h2>
        <p className="text-slate-600">
          {isAlreadyListed 
            ? "Select the number of SKUs you want to list (up to 2 SKUs)."
            : `Select the number of SKUs you want to list across your ${totalMachines} selected machine${totalMachines !== 1 ? 's' : ''} (up to 2 SKUs).`
          }
        </p>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Already listed?</strong> If you're already listed on these machines and only need to purchase retail media, you can skip this step by leaving SKUs at 0 and proceeding to the next step.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="space-y-6">
          {/* SKU Count Selection */}
          <div>
            <Label htmlFor="sku-count" className="text-base font-semibold text-slate-900 mb-3 block">
              Number of SKUs
            </Label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSKUCountChange(skuCount - 1)}
                  disabled={skuCount === 0}
                  className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors text-lg font-semibold"
                >
                  -
                </button>
                <Input
                  id="sku-count"
                  type="number"
                  min="0"
                  max="2"
                  value={skuCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    handleSKUCountChange(value);
                  }}
                  className="w-20 h-10 text-center text-lg font-semibold border-2 border-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => handleSKUCountChange(skuCount + 1)}
                  disabled={skuCount >= 2}
                  className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors text-lg font-semibold"
                >
                  +
                </button>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Package className="w-5 h-5" />
                <span className="text-sm">
                  {skuCount === 0 && 'No SKUs selected'}
                  {skuCount === 1 && '1 SKU selected'}
                  {skuCount === 2 && '2 SKUs selected'}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Maximum 2 SKUs allowed per campaign
            </p>
          </div>

          {/* Price Per SKU Display */}
          {skuCount > 0 && (
            <div>
              <Label className="text-base font-semibold text-slate-900 mb-3 block">
                Price Per SKU (£)
              </Label>
              <div className="bg-slate-50 rounded-lg border border-slate-200 px-4 py-3">
                {loadingPrice ? (
                  <div className="flex items-center gap-2 text-slate-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm">Loading price...</span>
                  </div>
                ) : pricePerSKU > 0 ? (
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-slate-900">
                      £{pricePerSKU.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-slate-500">
                      {selectedMachines.length > 0 && selectedMachines[0].venueType && (
                        <>Price for {selectedMachines[0].venueType} venue type</>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">
                    No listing price configured for selected venue type(s). Please contact an administrator.
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Listing price is set by administrators based on venue type
              </p>
            </div>
          )}

          {/* Listing Fee Display */}
          {skuCount > 0 && pricePerSKU > 0 && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Listing Fee</p>
                  <p className="text-xs text-blue-700 mt-1">
                    {skuCount} SKU{skuCount !== 1 ? 's' : ''} × £{pricePerSKU.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per SKU
                    {!isAlreadyListed && totalMachines > 0 && ` × ${totalMachines} machine${totalMachines !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-900">
                    £{listingFee.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

